import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, Modal } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import Question from './Question';
import ForceLandscape from './components/ForceLandscape';
import LoadingSpinner from './LoadingSpinner';
import { trackTrainingEvent } from './trainingTelemetry';

const Exam = ({ t, questions, trainingId, userId, isPreview, onRetakeCourse, startNewTraining, returnUrl }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState(null);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isPassed, setIsPassed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [nextTraining, setNextTraining] = useState(null);
  // Zamkniecie karty w trakcie egzaminu nie daje zaliczenia — przegladarka pokaze
  // wlasne ostrzezenie, zanim uzytkownik straci podejscie. Po wyswietleniu wyniku
  // (showThankYouModal) nie ma juz czego bronic.
  useEffect(() => {
    if (isPreview || showThankYouModal) {
      return undefined;
    }
    const ostrzez = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', ostrzez);
    return () => window.removeEventListener('beforeunload', ostrzez);
  }, [isPreview, showThankYouModal]);

  // No questions => material-only (no test). The end screen is then the final
  // completion gate ("Ukończ szkolenie"), reached after the material; with a
  // test it stays the answer-check gate.
  const hasTest = questions.length > 0;

  const handleAnswerSubmit = (answerId, questionId) => {
    trackTrainingEvent('answer_selected', 'exam-answer', 'Exam answer selected', {
      trainingId,
      questionId,
      answerId,
      questionIndex: currentQuestionIndex,
    });
    setAnswers({ ...answers, [questionId]: answerId });
    setCurrentQuestionIndex(currentQuestionIndex + 1);
  };

  const handleRetakeExam = () => {
    trackTrainingEvent('exam_retake', 'exam-retake', 'Exam retake', { trainingId });
    setCurrentQuestionIndex(0);
    setShowThankYouModal(false);
    onRetakeCourse();
  };

  const handleFinishExam = async () => {
    trackTrainingEvent('exam_submit', 'exam-submit', 'Exam submitted', {
      trainingId,
      answersCount: Object.keys(answers).length,
      isPreview,
    });
    setIsLoading(true);
    try {
      // Preview mode evaluates the answers WITHOUT recording anything (no user).
      const endpoint = isPreview ? 'preview-answers' : 'submit-answers';
      const payload = isPreview
        ? { training: trainingId, answers: answers }
        : { training: trainingId, userId: userId, answers: answers };
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}${endpoint}/${trainingId}`, payload);

      const { error, message, data, nextTraining: nt } = response.data;

      setShowThankYouModal(true);
      setModalMessage(t(message, data));
      setIsPassed(!error);
      setNextTraining(nt ?? null);
      trackTrainingEvent('exam_result', 'exam-result', 'Exam result', {
        trainingId,
        passed: !error,
        message,
        nextTraining: nt ?? null,
        isPreview,
      });

      // Auto-redirect after 3s when exam passed and no next training
      // (not in preview — the preview tab is closed on confirm instead).
      if (!isPreview && !error && !nt && returnUrl) {
        setTimeout(() => { window.location.href = returnUrl; }, 3000);
      }
    } catch (err) {
      setError(err);
      setModalMessage(err.response?.data?.error || 'Unknown error');
      trackTrainingEvent('exam_error', 'exam-error', 'Exam error', {
        trainingId,
        message: err.message,
        status: err.response?.status || null,
      });
    } finally {
      setIsLoading(false);
    }
  };
  

  const handleThankYouModalClose = () => {
    if (isPreview) {
      // Preview runs in a new tab opened from the panel — closing it returns the
      // creator to the system. Fall back to returnUrl/reload if close is blocked.
      window.close();
      if (returnUrl) {
        window.location.href = returnUrl;
      } else {
        window.location.reload();
      }
      return;
    }
    if (!nextTraining) {
      // All trainings completed — redirect to returnUrl or reload as fallback
      if (returnUrl) {
        window.location.href = returnUrl;
      } else {
        window.location.reload();
      }
    } else {
      setShowThankYouModal(false);
      onRetakeCourse();
      startNewTraining(nextTraining);
    }
  };

  const handleCloseErrorModal = () => {
    setError(null);
  };

  return (
    <div className="col">
      <LoadingSpinner isLoading={isLoading} />
      {currentQuestionIndex < questions.length ? (
        /* PYTANIA W TYM SAMYM POZIOMIE CO MATERIAL (Marcin, 2026-09-23): „treść pytania i
           odpowiedzi nie mieściły nam się na ekranie w wersji poziomej (...) bez konieczności
           skrolowania w dół i dochodzenia do przycisku Zatwierdź". Egzamin szedl dotad BEZ
           `ForceLandscape`, wiec po materiale ekran wracal do pionu, a kursant trzymajacy
           telefon poziomo dostawal tresc obrocona o 90°. Modale (wynik, podsumowanie) zostaja
           poza kadrem — dzialaly poprawnie i renderuja sie w portalu `body`, wiec obrot i tak
           by ich nie objal. Uklad zwiezly: `.lt-kadr-poziomy .lt-question-card` w index.css. */
        <ForceLandscape>
          <Question
            key={questions[currentQuestionIndex].id}
            question={questions[currentQuestionIndex].question}
            answers={questions[currentQuestionIndex].answers}
            onAnswerSubmit={handleAnswerSubmit}
            questionId={questions[currentQuestionIndex].id}
            imageUrl={questions[currentQuestionIndex].image_url}
          />
        </ForceLandscape>
      ) : (
        !showThankYouModal && (
          <Modal.Dialog>
            {/* Bez krzyzyka: nie byl podpiety pod zadna akcje (martwy), a sugerowal,
                ze da sie wyjsc z tego ekranu bez zatwierdzenia szkolenia. */}
            <Modal.Header>
              <Modal.Title>{hasTest ? t('no_more_questions_title') : t('finish_training_title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>{hasTest ? t('no_more_questions_description') : t('finish_training_description')}</p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="success" onClick={handleFinishExam}>
                {hasTest ? t('finish_exam') : t('finish_training')}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        )
      )}

      {/* BEZ przycisku zamkniecia i bez `onHide`: modal z krzyzykiem i tytulem
          „Dziekujemy" — pokazywanym rowniez po NIEZALICZENIU — sprawial, ze ludzie
          wychodzili przekonani, ze maja zaliczone (zgloszenie amwar162 2026-08-11).
          Tytul mowi teraz wprost, jaki jest wynik, a wyjscie prowadzi tylko przez
          swiadoma decyzje: zakoncz sesje albo powtorz egzamin. */}
      <Modal show={showThankYouModal} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title>{isPassed ? t('training_passed_title') : t('training_not_passed_title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{modalMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          {isPassed ? (
            <Button onClick={handleThankYouModalClose}>
              {nextTraining === null ? t('finish_session') : t('do_next_exam')}
            </Button>
          ) : (
            <Button variant="dark" onClick={handleRetakeExam}>
              {t('retake_exam')}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {error && (
        <Modal show={true} onHide={handleCloseErrorModal}>
          <Modal.Header closeButton>
            <Modal.Title>{t('error')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{error.message || 'An unexpected error occurred.'}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="danger" onClick={handleCloseErrorModal}>
              {t('close')}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default withTranslation()(Exam);
